import type {
  AuthResponse,
  CreateProjectInput,
  ProjectDto,
  UserDto,
} from '@doable/shared-types'

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000'

export type ApiError = {
  status: number
  code?: string
  title: string
  detail?: string
}

async function request<T>(
  path: string,
  init: RequestInit & { auth?: string } = {},
): Promise<T> {
  const headers = new Headers(init.headers)
  // Only set content-type when we actually have a body — Fastify rejects an
  // empty body with `application/json` header as 400 "Body cannot be empty".
  if (init.body) headers.set('content-type', 'application/json')
  if (init.auth) headers.set('authorization', `Bearer ${init.auth}`)

  const res = await fetch(`${API_URL}/v1${path}`, { ...init, headers })

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const data = text ? (JSON.parse(text) as unknown) : null

  if (!res.ok) {
    const err = (data ?? {}) as Partial<ApiError>
    throw {
      status: res.status,
      code: err.code,
      title: err.title ?? res.statusText,
      detail: err.detail,
    } satisfies ApiError
  }

  return data as T
}

export const api = {
  signup(input: {
    email: string
    password: string
    name: string
    /** Required only on the self-service path. Ignored when inviteToken set. */
    workspaceName?: string
    /** When set, the new user joins the invited tenant with the invited role. */
    inviteToken?: string
    captchaToken?: string
  }): Promise<AuthResponse> {
    return request('/auth/signup', { method: 'POST', body: JSON.stringify(input) })
  },
  login(input: { email: string; password: string; captchaToken?: string }): Promise<AuthResponse> {
    return request('/auth/login', { method: 'POST', body: JSON.stringify(input) })
  },
  /** Public — returns a viewer JWT for the curated demo tenant (60min). */
  startDemoSession(): Promise<AuthResponse> {
    return request('/auth/demo', { method: 'POST' })
  },
  logout(refreshToken: string): Promise<void> {
    return request('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    })
  },
  requestPasswordReset(email: string): Promise<void> {
    return request('/auth/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },
  resetPassword(token: string, password: string): Promise<void> {
    return request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    })
  },
  verifyEmail(token: string): Promise<void> {
    return request('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  },
  me(accessToken: string): Promise<{
    user: UserDto
    tenant: { id: string; slug: string; name: string; role: string } | null
  }> {
    return request('/me', { auth: accessToken })
  },
  listProjects(accessToken: string): Promise<{ projects: ProjectDto[] }> {
    return request('/projects', { auth: accessToken })
  },
  getProject(accessToken: string, id: string): Promise<ProjectDto> {
    return request(`/projects/${id}`, { auth: accessToken })
  },
  createProject(accessToken: string, input: CreateProjectInput): Promise<ProjectDto> {
    return request('/projects', {
      method: 'POST',
      auth: accessToken,
      body: JSON.stringify(input),
    })
  },
  deleteProject(accessToken: string, id: string): Promise<void> {
    return request(`/projects/${id}`, { method: 'DELETE', auth: accessToken })
  },
  resetProject(accessToken: string, id: string): Promise<void> {
    return request(`/projects/${id}/reset`, { method: 'POST', auth: accessToken })
  },
  /**
   * Start the project's draft dev server on the preview host ahead of the
   * first Send so there is no cold start. Idempotent; a 404 (never built)
   * is not an error for the caller.
   */
  async warmPreview(accessToken: string, id: string): Promise<void> {
    try {
      await request(`/projects/${id}/preview/warm`, { method: 'POST', auth: accessToken })
    } catch (err) {
      if ((err as ApiError).status === 404) return
      throw err
    }
  },
  getWorkspaceSettings(accessToken: string): Promise<{
    hasApiKey: boolean
    apiKeyHint: string | null
    hasGitlabToken: boolean
    gitlabHint: string | null
    freeBuilds: {
      remaining: number
      used: number
      costUsd: string
      managedAvailable: boolean
      unlimited: boolean
    }
  }> {
    return request('/workspace/settings', { auth: accessToken })
  },
  saveApiKey(
    accessToken: string,
    apiKey: string,
  ): Promise<{ hasApiKey: boolean; apiKeyHint: string }> {
    return request('/workspace/api-key', {
      method: 'PUT',
      auth: accessToken,
      body: JSON.stringify({ apiKey }),
    })
  },
  deleteApiKey(accessToken: string): Promise<void> {
    return request('/workspace/api-key', { method: 'DELETE', auth: accessToken })
  },
  saveGitlabToken(
    accessToken: string,
    token: string,
  ): Promise<{ hasGitlabToken: boolean; gitlabHint: string }> {
    return request('/workspace/gitlab-token', {
      method: 'PUT',
      auth: accessToken,
      body: JSON.stringify({ token }),
    })
  },
  deleteGitlabToken(accessToken: string): Promise<void> {
    return request('/workspace/gitlab-token', { method: 'DELETE', auth: accessToken })
  },
  exportToGitlab(accessToken: string, projectId: string): Promise<{ repoUrl: string }> {
    return request(`/projects/${projectId}/export-gitlab`, {
      method: 'POST',
      auth: accessToken,
    })
  },
  getMembers(accessToken: string): Promise<{
    members: Array<{ id: string; email: string; name: string | null; role: string; joinedAt: string }>
    pendingInvites: Array<{ id: string; email: string; role: string; createdAt: string; expiresAt: string }>
  }> {
    return request('/workspace/members', { auth: accessToken })
  },
  invite(accessToken: string, email: string, role: string): Promise<{ id: string; email: string; role: string }> {
    return request('/workspace/invite', {
      method: 'POST',
      auth: accessToken,
      body: JSON.stringify({ email, role }),
    })
  },
  acceptInvite(accessToken: string, token: string): Promise<{ tenantId: string; tenantName: string; role: string }> {
    return request('/workspace/accept-invite', {
      method: 'POST',
      auth: accessToken,
      body: JSON.stringify({ token }),
    })
  },
  changeMemberRole(
    accessToken: string,
    userId: string,
    role: 'admin' | 'editor' | 'viewer' | 'commenter',
  ): Promise<void> {
    return request(`/workspace/members/${userId}/role`, {
      method: 'PUT',
      auth: accessToken,
      body: JSON.stringify({ role }),
    })
  },
  removeMember(accessToken: string, userId: string): Promise<void> {
    return request(`/workspace/members/${userId}`, {
      method: 'DELETE',
      auth: accessToken,
    })
  },
  revokeInvite(accessToken: string, inviteId: string): Promise<void> {
    return request(`/workspace/invites/${inviteId}`, {
      method: 'DELETE',
      auth: accessToken,
    })
  },
  deleteWorkspace(
    accessToken: string,
    confirmSlug: string,
  ): Promise<{
    tenantId: string
    projectsDeleted: number
    domainsRemoved: number
    serversTerminated: number
    hetznerDisconnected: boolean
    gitlabCleared: boolean
  }> {
    return request('/workspace', {
      method: 'DELETE',
      auth: accessToken,
      body: JSON.stringify({ confirmSlug }),
    })
  },
  getCosts(accessToken: string): Promise<CostSummaryDto> {
    return request('/workspace/costs', { auth: accessToken })
  },
  updateSpendCap(
    accessToken: string,
    capUsd: number | null,
  ): Promise<{ capUsd: number | null }> {
    return request('/workspace/spend-cap', {
      method: 'PUT',
      auth: accessToken,
      body: JSON.stringify({ capUsd }),
    })
  },
  getAuditLog(accessToken: string): Promise<{
    entries: Array<{
      id: string
      userId: string | null
      action: string
      resourceType: string | null
      resourceId: string | null
      ipAddress: string | null
      createdAt: string
    }>
  }> {
    return request('/workspace/audit-log', { auth: accessToken })
  },
  listFiles(
    accessToken: string,
    projectId: string,
  ): Promise<{ files: Array<{ path: string; size: number }> }> {
    return request(`/projects/${projectId}/files`, { auth: accessToken })
  },
  readFile(
    accessToken: string,
    projectId: string,
    filePath: string,
  ): Promise<{ content: string }> {
    return request(
      `/projects/${projectId}/files/${encodeURIComponent(filePath)}`,
      { auth: accessToken },
    )
  },
  listBuilds(
    accessToken: string,
    projectId: string,
  ): Promise<{
    builds: Array<{
      id: string
      prompt: string
      status: string
      previewUrl: string | null
      durationMs: number | null
      costUsd: string | null
      startedAt: string
      finishedAt: string | null
      failureReason?: string | null
    }>
  }> {
    return request(`/projects/${projectId}/builds`, { auth: accessToken })
  },
  listSnapshots(
    accessToken: string,
    projectId: string,
  ): Promise<{ snapshots: SnapshotDto[] }> {
    return request(`/projects/${projectId}/snapshots`, { auth: accessToken })
  },
  listDeployments(
    accessToken: string,
    projectId: string,
  ): Promise<{ deployments: DeploymentDto[] }> {
    return request(`/projects/${projectId}/deployments`, { auth: accessToken })
  },

  // ── Hetzner integration ───────────────────────────────────────────────
  hetzner: {
    status(accessToken: string): Promise<HetznerStatusDto> {
      return request('/hetzner/status', { auth: accessToken })
    },
    connect(accessToken: string, apiToken: string): Promise<HetznerStatusDto> {
      return request('/hetzner/connect', {
        method: 'POST',
        auth: accessToken,
        body: JSON.stringify({ apiToken }),
      })
    },
    disconnect(accessToken: string): Promise<void> {
      return request('/hetzner', { method: 'DELETE', auth: accessToken })
    },
    listServers(accessToken: string): Promise<{ servers: HetznerServerDto[] }> {
      return request('/hetzner/servers', { auth: accessToken })
    },
    listServerTypes(accessToken: string): Promise<{ types: HetznerServerTypeDto[] }> {
      return request('/hetzner/server-types', { auth: accessToken })
    },
    provisionStaging(accessToken: string): Promise<HetznerServerDto> {
      return request('/hetzner/staging/provision', {
        method: 'POST',
        auth: accessToken,
      })
    },
    listSnapshots(
      accessToken: string,
      serverId: string,
    ): Promise<{
      snapshots: HetznerSnapshotDto[]
      automatedBackupsEnabled: boolean
    }> {
      return request(`/hetzner/servers/${serverId}/snapshots`, {
        auth: accessToken,
      })
    },
    createSnapshot(
      accessToken: string,
      serverId: string,
      description?: string,
    ): Promise<HetznerSnapshotDto> {
      return request(`/hetzner/servers/${serverId}/snapshots`, {
        method: 'POST',
        auth: accessToken,
        body: JSON.stringify(description ? { description } : {}),
      })
    },
    setAutomatedBackups(
      accessToken: string,
      serverId: string,
      enabled: boolean,
    ): Promise<{ enabled: boolean }> {
      return request(`/hetzner/servers/${serverId}/backups`, {
        method: 'PUT',
        auth: accessToken,
        body: JSON.stringify({ enabled }),
      })
    },
    restoreFromSnapshot(
      accessToken: string,
      serverId: string,
      imageId: number,
    ): Promise<{ ok: true }> {
      return request(`/hetzner/servers/${serverId}/restore`, {
        method: 'POST',
        auth: accessToken,
        body: JSON.stringify({ imageId }),
      })
    },
  },

  // ── Platform admin (env-allowlisted) ───────────────────────────────────
  admin: {
    listTenants(accessToken: string): Promise<{ tenants: AdminTenantDto[] }> {
      return request('/admin/tenants', { auth: accessToken })
    },
    listServers(accessToken: string): Promise<{ servers: AdminServerDto[] }> {
      return request('/admin/servers', { auth: accessToken })
    },
    listSignups(accessToken: string, limit = 50): Promise<{ signups: AdminSignupDto[] }> {
      return request(`/admin/signups?limit=${limit}`, { auth: accessToken })
    },
    listDrift(
      accessToken: string,
      opts: { onlyDrift?: boolean; limit?: number } = {},
    ): Promise<{ runs: AdminDriftDto[] }> {
      const params = new URLSearchParams()
      if (opts.onlyDrift !== undefined) params.set('onlyDrift', String(opts.onlyDrift))
      if (opts.limit !== undefined) params.set('limit', String(opts.limit))
      const qs = params.toString()
      return request(`/admin/drift${qs ? `?${qs}` : ''}`, { auth: accessToken })
    },
  },

  // ── Comments ──────────────────────────────────────────────────────────
  comments: {
    list(accessToken: string, projectId: string): Promise<{ comments: CommentDto[] }> {
      return request(`/projects/${projectId}/comments`, { auth: accessToken })
    },
    create(
      accessToken: string,
      projectId: string,
      body: string,
    ): Promise<CommentDto> {
      return request(`/projects/${projectId}/comments`, {
        method: 'POST',
        auth: accessToken,
        body: JSON.stringify({ body }),
      })
    },
    remove(accessToken: string, commentId: string): Promise<void> {
      return request(`/comments/${commentId}`, {
        method: 'DELETE',
        auth: accessToken,
      })
    },
  },

  // ── Domains ───────────────────────────────────────────────────────────
  domains: {
    list(accessToken: string, projectId: string): Promise<{ domains: DomainDto[] }> {
      return request(`/projects/${projectId}/domains`, { auth: accessToken })
    },
    add(
      accessToken: string,
      projectId: string,
      input: { domain: string; serverType?: string },
    ): Promise<DomainDto> {
      return request(`/projects/${projectId}/domains`, {
        method: 'POST',
        auth: accessToken,
        body: JSON.stringify(input),
      })
    },
    remove(accessToken: string, domainId: string): Promise<void> {
      return request(`/domains/${domainId}`, {
        method: 'DELETE',
        auth: accessToken,
      })
    },
    checkDns(
      accessToken: string,
      domainId: string,
    ): Promise<{
      resolved: boolean
      expected: string | null
      actual: string[]
      matched: boolean
    }> {
      return request(`/domains/${domainId}/check-dns`, {
        method: 'POST',
        auth: accessToken,
      })
    },
  },

  // ── GitLab integration ────────────────────────────────────────────────
  gitlab: {
    status(accessToken: string): Promise<{ connected: boolean; configured: boolean }> {
      return request('/git/gitlab/status', { auth: accessToken })
    },
    /**
     * Get the GitLab authorize URL. The caller then does
     *   window.location.href = res.authorizeUrl
     * to navigate the browser to GitLab. Browsers drop headers on top-level
     * navigations, so a JSON-returning POST is the right fit instead of a
     * server-side redirect.
     */
    initiate(accessToken: string): Promise<{ authorizeUrl: string }> {
      return request('/git/gitlab/oauth/initiate', {
        method: 'POST',
        auth: accessToken,
      })
    },
    disconnect(accessToken: string): Promise<void> {
      return request('/git/gitlab', { method: 'DELETE', auth: accessToken })
    },
    listRepos(accessToken: string): Promise<{ repos: GitlabRepoDto[] }> {
      return request('/git/gitlab/repos', { auth: accessToken })
    },
  },
}

export type HetznerStatusDto = {
  connected: boolean
  accountLabel: string | null
  hetznerAccountId: number | null
  monthlyCostCapEur: number | null
  lastValidatedAt: string | null
  serverCount: number
  monthlyCostEur: number
  stagingStatus: 'none' | 'creating' | 'running' | 'error'
}

export type HetznerServerDto = {
  id: string
  hetznerServerId: number
  hetznerName: string
  serverType: string
  publicIpv4: string | null
  role: 'staging' | 'prod'
  boundDomain: string | null
  monthlyCostEur: number
  status: string
  provisionedAt: string
}

export type HetznerServerTypeDto = {
  name: string
  cores: number
  memoryGb: number
  diskGb: number
  monthlyEur: number
}

export type HetznerSnapshotDto = {
  id: number
  description: string | null
  createdAt: string | null
  status: string
  imageSize: number | null
  type: 'snapshot' | 'backup'
}

export type GitlabRepoDto = {
  id: number
  pathWithNamespace: string
  name: string
  webUrl: string
  defaultBranch: string | null
  visibility: string
  lastActivityAt: string
}

export type SnapshotDto = {
  id: string
  buildId: string | null
  conversationId: string | null
  gitSha: string
  parentSha: string | null
  prompt: string | null
  summary: string | null
  filesChanged: number
  costUsd: string | null
  durationMs: number | null
  numTurns: number | null
  createdAt: string
}

export type CommentDto = {
  id: string
  body: string
  createdAt: string
  author: {
    id: string
    name: string | null
    email: string
  }
}

export type AdminTenantDto = {
  id: string
  slug: string
  name: string
  status: string
  createdAt: string
  memberCount: number
  serverCount: number
  monthlyCostEur: number
  anthropicLast30dUsd: number
}

export type AdminServerDto = {
  id: string
  tenantId: string
  tenantSlug: string
  hetznerServerId: number
  hetznerName: string
  serverType: string
  publicIpv4: string | null
  role: 'staging' | 'prod'
  boundDomain: string | null
  monthlyCostEur: number
  status: string
  provisionedAt: string
}

export type AdminSignupDto = {
  userId: string
  email: string
  name: string | null
  createdAt: string
  tenantSlug: string | null
  role: string | null
}

export type AdminDriftDto = {
  id: string
  tenantId: string
  tenantSlug: string
  ranAt: string
  serversInDoable: number
  serversInHetzner: number
  driftDetected: boolean
  driftDetails: unknown | null
  durationMs: number | null
}

export type CostSummaryDto = {
  anthropic: {
    last30dUsd: number
    allTimeUsd: number
    monthlyCapUsd: number | null
    capExceeded: boolean
    topProjectsLast30d: Array<{
      projectId: string
      projectName: string
      costUsd: number
    }>
  }
  freeBuilds: {
    remaining: number
    used: number
    costUsd: string
    managedAvailable: boolean
    unlimited: boolean
  }
  hetzner: {
    monthlyCostEur: number
    serverCount: number
  }
}

export type DomainDto = {
  id: string
  domain: string
  projectId: string | null
  status: string  // configuring | dns_pending | cert_pending | live | suspended
  certStatus: string | null
  lastDnsCheckAt: string | null
  addedAt: string
  prodServer: {
    id: string
    publicIpv4: string | null
    serverType: string
    status: string
  } | null
}

export type DeploymentDto = {
  id: string
  snapshotId: string
  status: string  // queued | building | live | failed | superseded
  url: string | null
  errorReason: string | null
  triggeredBy: string | null
  triggeredByEmail: string | null
  rollbackOf: string | null
  durationMs: number | null
  startedAt: string
  finishedAt: string | null
  snapshotSummary: string | null
  snapshotGitSha: string
  snapshotPrompt: string | null
}


/**
 * Draft/live delivery fields the control plane adds to ProjectDto (computed
 * from the builds table). Optional so the app keeps working against an older
 * API: a Send (edit-only turn) updates the hot-reloading draft preview on
 * `<slug>-draft.dev.doable.website`; Publish (deploy) updates the live site.
 */
export type ProjectDraftFields = {
  draftUrl?: string | null
  draftUpdatedAt?: string | null
  hasUnpublishedChanges?: boolean
}
export type ProjectWithDraft = import('@doable/shared-types').ProjectDto & ProjectDraftFields
